/**
 * StepDetailPanel — Rechte Spalte des UI-Builders. Edit-Panel für einen Step.
 * @since v2.514.0 (#1173)
 *
 * Props:
 *   - step: aktiver Step
 *   - priorSteps: vorherige Steps (für VariablePicker steps-Scope)
 *   - catalog: { variable_scopes, builtins, action_types, operators, skill_library }
 *   - onChange(updatedStep)
 *   - onTestStep()
 *   - validationErrors: string[] (nur Errors die diesen Step betreffen)
 */
import { useState } from 'react';
import { Button } from '../ui';
import SkillSelector from './SkillSelector';
import VariablePicker from './VariablePicker';
import ConditionBuilder from './ConditionBuilder';

export default function StepDetailPanel({ step, priorSteps = [], catalog = {}, onChange, onTestStep, validationErrors = [] }) {
  const [inputsJson, setInputsJson] = useState(() => JSON.stringify(step?.inputs || {}, null, 2));
  const [inputsErr, setInputsErr] = useState(null);

  if (!step) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center py-12">
        <div className="text-sm text-[var(--text-tertiary)]">Wähle einen Step links aus<br/>oder füge einen neuen hinzu.</div>
      </div>
    );
  }

  function setField(key, val) {
    onChange && onChange({ ...step, [key]: val });
  }

  function handleSkillChange(picked) {
    const next = { ...step };
    delete next.skill_id;
    delete next.skill_slug;
    delete next.action;
    delete next.action_type;
    if (picked.kind === 'skill') {
      delete next.type;
      next.skill_id = picked.skill_id;
      if (picked.skill_slug) next.skill_slug = picked.skill_slug;
    } else if (picked.kind === 'builtin') {
      next.type = 'builtin';
      next.action = picked.action;
    } else if (picked.kind === 'action') {
      next.type = 'action';
      next.action_type = picked.action_type;
    }
    onChange && onChange(next);
  }

  function currentSkillValue() {
    if (step.skill_id) return { kind: 'skill', skill_id: step.skill_id, skill_slug: step.skill_slug };
    if (step.type === 'builtin') return { kind: 'builtin', action: step.action };
    if (step.type === 'action') return { kind: 'action', action_type: step.action_type };
    return { kind: 'skill' };
  }

  function commitInputs() {
    try {
      const parsed = JSON.parse(inputsJson || '{}');
      // On-Blur Mustache-Ref-Check (Phase 4): warne bei Forward-Refs
      const refErr = checkInlineRefs(parsed, priorSteps);
      if (refErr) {
        setInputsErr(refErr);
      } else {
        setInputsErr(null);
      }
      setField('inputs', parsed);
    } catch (e) {
      setInputsErr(String(e.message || e));
    }
  }

  function insertIntoInputs(tpl) {
    const next = (inputsJson || '').replace(/("[^"]*")\s*$/, `$1${tpl}`);
    setInputsJson(next || tpl);
  }

  return (
    <div className="space-y-4">
      {validationErrors.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2 text-xs">
          <div className="font-medium mb-1">Validation-Fehler</div>
          <ul className="list-disc list-inside space-y-0.5">
            {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Step-ID">
          <input
            value={step.id || ''}
            onChange={(e) => setField('id', e.target.value.replace(/[^a-zA-Z0-9_-]/g, '_'))}
            className="w-full px-2 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-xs font-mono"
            placeholder="z.B. classify_intent"
          />
        </Field>
        <Field label="Beschreibung (optional)">
          <input
            value={step.description || ''}
            onChange={(e) => setField('description', e.target.value)}
            className="w-full px-2 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-xs"
          />
        </Field>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary)] font-medium mb-2">Step-Kind</div>
        <SkillSelector
          value={currentSkillValue()}
          library={catalog.skill_library || { prompt_template: [], tool_call: [] }}
          builtins={catalog.builtins || []}
          actionTypes={catalog.action_types || []}
          onChange={handleSkillChange}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary)] font-medium">Inputs (JSON)</div>
          <VariablePicker scopes={catalog.variable_scopes || []} priorSteps={priorSteps} onPick={insertIntoInputs} />
        </div>
        <textarea
          value={inputsJson}
          onChange={(e) => setInputsJson(e.target.value)}
          onBlur={commitInputs}
          rows={6}
          className={`w-full px-2 py-1.5 border rounded text-xs font-mono focus:outline-none focus:border-[#338edc] ${inputsErr ? 'border-rose-300' : 'border-[var(--border-subtle,#E5E7EB)]'}`}
          placeholder='{ "case_id": "{{event.case_id}}" }'
        />
        {inputsErr && <div className="text-[10px] text-rose-600 mt-1">JSON-Fehler: {inputsErr}</div>}
      </div>

      <ConditionBuilder
        label="condition (Step nur ausführen wenn…)"
        value={step.condition || ''}
        onChange={(v) => setField('condition', v || undefined)}
        operators={catalog.operators || []}
      />
      <ConditionBuilder
        label="early_return_if (Pipeline abbrechen wenn…)"
        value={step.early_return_if || ''}
        onChange={(v) => setField('early_return_if', v || undefined)}
        operators={catalog.operators || []}
      />

      <div className="flex items-center justify-between pt-2 border-t border-[var(--border-subtle,#E5E7EB)]">
        <div className="text-[10px] text-[var(--text-tertiary)]">
          Test-Run nutzt Stub-Skills + Sandbox-Mode — keine externen Calls.
        </div>
        <Button variant="ghost" onClick={onTestStep}>Step testen</Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary)] mb-1 font-medium">{label}</div>
      {children}
    </label>
  );
}

// Phase 4 (#1173): on-blur Ref-Check ohne Backend-Roundtrip.
// Sucht in den Inputs nach {{steps.X.outputs.Y}} und prüft, ob X in priorSteps existiert.
function checkInlineRefs(obj, priorSteps) {
  const re = /\{\{\s*steps\.([a-zA-Z0-9_-]+)\.outputs\.([a-zA-Z0-9_.-]+)\s*\}\}/g;
  const blob = JSON.stringify(obj);
  const priorIds = new Set(priorSteps.map((s) => s.id));
  let m;
  while ((m = re.exec(blob)) !== null) {
    if (!priorIds.has(m[1])) {
      return `Referenz steps.${m[1]}.outputs.${m[2]} — Step '${m[1]}' kommt nicht VOR diesem Step.`;
    }
  }
  return null;
}
